import API_URL from "../config/api";
import { getCookie, deleteCookie } from "../utils/cookies";
import { getUserAgent } from "../utils/userAgent";
import webPlayerStorage from "../utils/webPlayerStorage";

class ApiService {
  constructor() {
    this.baseURL = API_URL;
  }

  async webPlayerRequest(endpoint, options = {}) {
    const webPlayerToken = webPlayerStorage.getToken();

    const config = {
      ...options,
      headers: {
        ...options.headers,
        "X-Lunova-User-Agent": getUserAgent(),
      },
    };

    if (options.body || ["POST", "PUT", "PATCH"].includes(options.method)) {
      config.headers["Content-Type"] = "application/json";
    }

    if (
      (options.method === "POST" ||
        options.method === "PUT" ||
        options.method === "PATCH") &&
      !options.body
    ) {
      config.body = JSON.stringify({});
    }

    // Send token or "NoAuth" for public access to trending content
    config.headers.Authorization = `Bearer ${webPlayerToken || "NoAuth"}`;

    const url = `${this.baseURL}${endpoint}`;

    let response;

    try {
      response = await fetch(url, config);
    } catch (error) {
      throw error;
    }

    if (!response.ok) {
      if (response.status === 401) {
        webPlayerStorage.removeToken();
      }

      const errorData = await response.json().catch(() => ({
        error: `API Error: ${response.statusText}`,
      }));

      throw new Error(errorData.error || `API Error: ${response.statusText}`);
    }

    return response.json();
  }

  async request(
    endpoint,
    options = {},
    { redirectOnUnauthorized = true } = {}
  ) {
    const token = getCookie("authToken");

    const config = {
      ...options,
      headers: {
        ...options.headers,
      },
    };

    if (options.body || ["POST", "PUT", "PATCH"].includes(options.method)) {
      config.headers["Content-Type"] = "application/json";
    }

    if (
      (options.method === "POST" ||
        options.method === "PUT" ||
        options.method === "PATCH") &&
      !options.body
    ) {
      config.body = JSON.stringify({});
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const url = `${this.baseURL}${endpoint}`;

    let response;

    try {
      response = await fetch(url, config);
    } catch (error) {
      throw error;
    }

    if (!response.ok) {
      if (response.status === 401) {
        deleteCookie("authToken");
        if (redirectOnUnauthorized) {
          window.location.href = "/web-player/login";
        }
      }

      const errorData = await response.json().catch(() => ({
        error: `API Error: ${response.statusText}`,
      }));

      const error = new Error(
        errorData.error || `API Error: ${response.statusText}`
      );

      error.status = response.status;
      throw error;
    }

    return response.json();
  }

  async validateToken(token) {
    try {
      const response = await fetch(
        `${this.baseURL}/v2/dashboard/validate-token`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();

      if (response.ok) {
        return data;
      }

      throw new Error("Invalid or expired token");
    } catch (error) {
      throw error;
    }
  }

  async getPlaylists() {
    const token = getCookie("authToken");

    if (!token) {
      const error = new Error(
        "Your dashboard session is unavailable. Please return to the dashboard and sign in again."
      );

      error.status = 401;
      throw error;
    }

    const response = await this.request(
      "/v1/dashboard/accounts/playlists",
      {},
      { redirectOnUnauthorized: false }
    );
    const payload = response?.data;

    if (!payload || Array.isArray(payload)) {
      return response;
    }

    const accounts = payload.accounts || [];
    const playlists =
      payload.playlists ||
      accounts.flatMap((account) => account.playlists || []);

    return {
      ...response,
      accounts,
      data: playlists,
      maxPlaylistsPerAccount: payload.maxPlaylistsPerAccount,
    };
  }

  async getAvatarsDirect() {
    return this.request("/v1/avatars");
  }

  // ============================================
  // WebPlayer Specific API Methods
  // ============================================
  // These methods use webPlayerRequest and are specifically for WebPlayer functionality

  async webPlayerLogin(credentials) {
    if (credentials.code) {
      return this.webPlayerRequest("/v2/login/lunakey", {
        method: "POST",
        body: JSON.stringify({
          code: credentials.code,
        }),
      });
    } else {
      const { url, username, password } = credentials;
      const uriHost = url.startsWith("http") ? url : `https://${url}`;

      return this.webPlayerRequest("/v2/login", {
        method: "POST",
        body: JSON.stringify({
          uriHost,
          username,
          password,
        }),
      });
    }
  }

  async webPlayerLoginProfile(profileData) {
    return this.webPlayerRequest("/v2/login/profile", {
      method: "POST",
      body: JSON.stringify(profileData),
    });
  }

  async webPlayerGetProfiles() {
    return this.webPlayerRequest("/v2/user/profiles");
  }

  async webPlayerGetHome() {
    return this.webPlayerRequest("/v2/home");
  }

  async webPlayerGetMovies(params = {}) {
    const queryString = new URLSearchParams(params).toString();

    return this.webPlayerRequest(
      `/v2/movies${queryString ? `?${queryString}` : ""}`
    );
  }

  async webPlayerGetMovie(movieId) {
    return this.webPlayerRequest(`/v2/movies/${movieId}`);
  }

  async webPlayerGetSeries(params = {}) {
    const queryString = new URLSearchParams(params).toString();

    return this.webPlayerRequest(
      `/v2/series${queryString ? `?${queryString}` : ""}`
    );
  }

  async webPlayerGetSeriesDetails(seriesId) {
    return this.webPlayerRequest(`/v2/series/${seriesId}`);
  }

  async webPlayerGetCastDetails(castId) {
    return this.webPlayerRequest(`/v2/cast/${castId}`);
  }

  async webPlayerGetLiveTV(params = {}) {
    const queryString = new URLSearchParams(params).toString();

    return this.webPlayerRequest(
      `/v2/live-tv${queryString ? `?${queryString}` : ""}`
    );
  }

  async webPlayerGetLiveTVCategories() {
    return this.webPlayerRequest("/v2/live-tv/categories");
  }

  async webPlayerGetEPG(params = {}) {
    const queryString = new URLSearchParams(params).toString();

    return this.webPlayerRequest(
      `/v2/epg${queryString ? `?${queryString}` : ""}`
    );
  }

  async webPlayerSearch(params = {}) {
    const queryString = new URLSearchParams(params).toString();

    return this.webPlayerRequest(
      `/v2/search${queryString ? `?${queryString}` : ""}`
    );
  }

  async webPlayerGetBookmarks() {
    return this.webPlayerRequest("/v2/bookmarks");
  }

  async webPlayerCreateBookmark(mediaId, mediaType) {
    return this.webPlayerRequest("/v2/bookmarks", {
      method: "POST",
      body: JSON.stringify({ mediaId, mediaType }),
    });
  }

  async webPlayerDeleteBookmark(mediaId, mediaType) {
    return this.webPlayerRequest("/v2/bookmarks/delete", {
      method: "POST",
      body: JSON.stringify({ mediaId, mediaType }),
    });
  }

  async webPlayerReorderBookmarks(mediaType, items, playlistId) {
    return this.webPlayerRequest("/v2/bookmarks/reorder", {
      method: "PATCH",
      body: JSON.stringify({ mediaType, items, playlistId }),
    });
  }

  async webPlayerGetRecentlyWatched() {
    return this.webPlayerRequest("/v2/recently-watched");
  }

  async webPlayerUpdateRecentlyWatched(mediaData) {
    // Use PATCH method to match Android app's patchToApi behavior
    return this.webPlayerRequest("/v2/recently-watched", {
      method: "PATCH",
      body: JSON.stringify(mediaData),
    });
  }

  async webPlayerMarkAsWatched(mediaId, mediaType) {
    return this.webPlayerRequest("/v2/recently-watched/mark-watched", {
      method: "POST",
      body: JSON.stringify({ mediaId, mediaType }),
    });
  }

  async webPlayerHeartbeat() {
    return this.webPlayerRequest("/v2/heartbeat");
  }

  async webPlayerGetAvatars() {
    return this.webPlayerRequest("/v1/avatars");
  }

  async webPlayerCreateProfile(profileData) {
    return this.webPlayerRequest("/v2/user/profile", {
      method: "POST",
      body: JSON.stringify(profileData),
    });
  }

  async webPlayerUpdateProfile(profileId, profileData) {
    return this.webPlayerRequest(`/v2/user/profile/${profileId}`, {
      method: "PATCH",
      body: JSON.stringify(profileData),
    });
  }

  async webPlayerDeleteProfile(profileId) {
    return this.webPlayerRequest(`/v2/user/profile/${profileId}`, {
      method: "DELETE",
    });
  }

  async webPlayerSetupStepOne() {
    return this.webPlayerRequest("/v2/setup", {
      method: "POST",
    });
  }

  async webPlayerSetupStepTwo() {
    return this.webPlayerRequest("/v2/setup/cache", {
      method: "POST",
    });
  }

  async webPlayerRedeemCode(code) {
    return this.webPlayerRequest("/v2/dashboard/redeem-codes/login", {
      method: "POST",
      body: JSON.stringify({ code }),
    });
  }

  // ============================================
  // Sports API Methods (V2)
  // ============================================

  async getSportsHome(timestamp, timezone) {
    const params = new URLSearchParams();

    if (timestamp) params.append("timestamp", timestamp);
    if (timezone) params.append("timezone", timezone);

    return this.webPlayerRequest(
      `/v2/sports/home${params.toString() ? `?${params.toString()}` : `?timestamp=${new Date().toISOString()}`}`
    );
  }

  async getUpcomingEvents() {
    return this.webPlayerRequest("/v2/sports/events/upcoming");
  }

  async getPastEvents() {
    return this.webPlayerRequest("/v2/sports/events/past");
  }

  async getSportsEvent(mediaId, sport) {
    const params = new URLSearchParams();

    if (sport) params.append("sport", sport);

    return this.webPlayerRequest(
      `/v2/sports/events/live/${mediaId}${params.toString() ? `?${params.toString()}` : ""}`
    );
  }

  // Standings — Android: GET /v2/sports/standings/:sport?league=:leagueId
  async getSportStandings(sport, leagueId) {
    const params = new URLSearchParams();

    if (leagueId) params.append("league", leagueId);

    return this.webPlayerRequest(
      `/v2/sports/standings/${sport}${params.toString() ? `?${params.toString()}` : ""}`
    );
  }

  // Fixtures — Android: GET /v2/sports/fixtures/:sport/:leagueId (leagueId optional for some sports)
  async getSportFixtures(sport, leagueId, date, timestamp, timezone) {
    const params = new URLSearchParams();

    if (date) params.append("date", date);
    if (timestamp) params.append("timestamp", timestamp);
    if (timezone) params.append("timezone", timezone);

    const path = leagueId
      ? `/v2/sports/fixtures/${sport}/${leagueId}`
      : `/v2/sports/fixtures/${sport}`;

    return this.webPlayerRequest(
      `${path}${params.toString() ? `?${params.toString()}` : ""}`
    );
  }

  // Soccer-specific shortcut (kept for backward compat with existing call sites)
  async getSoccerFixtures(leagueId, date, timestamp, timezone) {
    return this.getSportFixtures("soccer", leagueId, date, timestamp, timezone);
  }

  // Categories
  async getSportsCategories() {
    return this.webPlayerRequest("/v2/sports/categories");
  }

  async getSportsCategory(categoryKey) {
    return this.webPlayerRequest(`/v2/sports/category/${categoryKey}`);
  }

  // Hero / Live / Highlights / Hall of Fame
  async getSportsHero() {
    return this.webPlayerRequest("/v2/sports/hero");
  }

  async getSportsLive() {
    return this.webPlayerRequest("/v2/sports/live");
  }

  async getSportsHighlights(sport) {
    const params = new URLSearchParams();

    if (sport) params.append("sport", sport);

    return this.webPlayerRequest(
      `/v2/sports/highlights${params.toString() ? `?${params.toString()}` : ""}`
    );
  }

  async getSportsHallOfFame() {
    return this.webPlayerRequest("/v2/sports/hall-of-fame");
  }

  // Game detail
  async getSportsGame(sport, gameId) {
    return this.webPlayerRequest(`/v2/sports/game/${sport}/${gameId}`);
  }

  // League detail
  async getSportsLeague(sport, leagueId, name, country) {
    const params = new URLSearchParams();

    if (name) params.append("name", name);
    if (country) params.append("country", country);

    return this.webPlayerRequest(
      `/v2/sports/league/${sport}/${leagueId}${params.toString() ? `?${params.toString()}` : ""}`
    );
  }

  async getSportsLeagueTeams(sport, leagueId) {
    return this.webPlayerRequest(
      `/v2/sports/league/${sport}/${leagueId}/teams`
    );
  }

  async getSportsLeagueStats(sport, leagueId) {
    return this.webPlayerRequest(
      `/v2/sports/league/${sport}/${leagueId}/stats`
    );
  }

  // Team detail
  async getSportsTeamProfile(sport, teamId) {
    return this.webPlayerRequest(`/v2/sports/team/${sport}/${teamId}/profile`);
  }

  async getTeamsSearch(query) {
    return this.webPlayerRequest(
      `/v2/sports/teams/search?q=${encodeURIComponent(query)}`
    );
  }

  async getTeamsSuggested() {
    return this.webPlayerRequest("/v2/sports/teams/suggested");
  }

  // Favorites — teams
  async getMyTeams() {
    return this.webPlayerRequest("/v2/sports/my-teams");
  }

  async addFavoriteTeam(payload) {
    return this.webPlayerRequest("/v2/sports/favorites/team", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async removeFavoriteTeam(teamId) {
    return this.webPlayerRequest(`/v2/sports/favorites/team/${teamId}`, {
      method: "DELETE",
    });
  }

  // Favorites — players
  async getMyPlayers() {
    return this.webPlayerRequest("/v2/sports/my-players");
  }

  async getPlayerProfile(playerId) {
    return this.webPlayerRequest(`/v2/sports/players/${playerId}/profile`);
  }

  async getPlayersSearch(query) {
    return this.webPlayerRequest(
      `/v2/sports/players/search?q=${encodeURIComponent(query)}`
    );
  }

  async getPlayersSuggested() {
    return this.webPlayerRequest("/v2/sports/players/suggested");
  }

  async addFavoritePlayer(payload) {
    return this.webPlayerRequest("/v2/sports/favorites/player", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async removeFavoritePlayer(playerId) {
    return this.webPlayerRequest(`/v2/sports/favorites/player/${playerId}`, {
      method: "DELETE",
    });
  }

  // ============================================
  // Subtitles API Methods
  // ============================================

  async webPlayerGetMovieSubtitles(mediaId) {
    return this.webPlayerRequest(`/v2/movies/${mediaId}/subtitles`);
  }

  async webPlayerGetSeriesSubtitles(mediaId, episodeId) {
    return this.webPlayerRequest(
      `/v2/series/${mediaId}/subtitles/${episodeId}`
    );
  }

  // ============================================
  // Reminders API Methods
  // ============================================

  // Use separate URL for reminders (for local testing)
  get remindersBaseURL() {
    return process.env.REACT_APP_REMINDERS_API_URL || this.baseURL;
  }

  async remindersRequest(endpoint, options = {}) {
    const webPlayerToken = webPlayerStorage.getToken();

    const config = {
      ...options,
      headers: {
        ...options.headers,
        "X-Lunova-User-Agent": getUserAgent(),
      },
    };

    if (options.body || ["POST", "PUT", "PATCH"].includes(options.method)) {
      config.headers["Content-Type"] = "application/json";
    }

    if (webPlayerToken) {
      config.headers.Authorization = `Bearer ${webPlayerToken}`;
    }

    const url = `${this.remindersBaseURL}${endpoint}`;
    const response = await fetch(url, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        error: `API Error: ${response.statusText}`,
      }));

      throw new Error(errorData.error || `API Error: ${response.statusText}`);
    }

    return response.json();
  }

  async webPlayerGetReminders() {
    return this.remindersRequest("/v2/reminders");
  }

  async webPlayerCreateReminder(reminderData) {
    return this.remindersRequest("/v2/reminders", {
      method: "POST",
      body: JSON.stringify(reminderData),
    });
  }

  async webPlayerDeleteReminder(reminderId) {
    return this.remindersRequest(`/v2/reminders/${reminderId}`, {
      method: "DELETE",
    });
  }

  async webPlayerRegisterPushSubscription(
    subscription,
    platform = "web",
    deviceInfo = {}
  ) {
    return this.remindersRequest("/v2/reminders/push/register", {
      method: "POST",
      body: JSON.stringify({ subscription, platform, deviceInfo }),
    });
  }

  async webPlayerUnregisterPushSubscription(endpoint) {
    return this.remindersRequest("/v2/reminders/push/unregister", {
      method: "POST",
      body: JSON.stringify({ endpoint }),
    });
  }
}

const apiService = new ApiService();

export default apiService;
